import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft, ChevronRight, Car, Briefcase, Coffee, Hotel,
  Utensils, Users, Loader2, Calendar, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m > 0 ? m + "m" : ""}`.trim();
  return `${m}m`;
}

function fmtDurationFull(seconds) {
  if (!seconds || seconds <= 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function getWeekString(date) {
  const d = new Date(date);
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const oneDay = 86400000;
  const dayOfYear = Math.floor((d - new Date(d.getFullYear(), 0, 1)) / oneDay) + 1;
  const dayOfWeek = d.getDay() || 7;
  const weekNum = Math.ceil((dayOfYear - dayOfWeek + 10) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

function getMondayOfWeek(weekStr) {
  const [yr, wk] = weekStr.split("-W").map(Number);
  const jan4 = new Date(yr, 0, 4);
  const startOfW1 = new Date(jan4);
  startOfW1.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
  const monday = new Date(startOfW1);
  monday.setDate(startOfW1.getDate() + (wk - 1) * 7);
  return monday;
}

const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const STATE_LABELS = {
  abgeschlossen: "Abg.",
  hotel: "Hotel",
  baustelle: "Baust.",
  unterwegs: "Unt.",
  zuhause: "—",
  "—": "—",
};

export default function Wochenansicht() {
  const [week, setWeek] = useState(() => getWeekString(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/events/weekly-report?week=${week}`);
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [week]);

  useEffect(() => { loadData(); }, [loadData]);

  const shiftWeek = (dir) => {
    const monday = getMondayOfWeek(week);
    monday.setDate(monday.getDate() + dir * 7);
    setWeek(getWeekString(monday));
  };

  const monday = getMondayOfWeek(week);

  return (
    <div className="space-y-4" data-testid="wochenansicht-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--tga-blue)", opacity: 0.15 }}>
            <Calendar className="w-5 h-5" style={{ color: "var(--tga-blue)" }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: "var(--tga-text-primary)" }}>Wochenuebersicht</h1>
            <p className="text-xs" style={{ color: "var(--tga-text-muted)" }}>Fahrten, Arbeitszeiten & Verpflegung aller Mitarbeiter</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={async () => {
              try {
                const res = await authFetch(`${API}/events/weekly-report/pdf?week=${week}`);
                if (!res.ok) { alert("PDF-Fehler"); return; }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Wochenbericht_${week}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch { alert("PDF-Download fehlgeschlagen"); }
            }}
            className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }}
            title="PDF Export"
            data-testid="pdf-export-btn"
          >
            <Download className="w-4 h-4" />
          </button>
          <button onClick={() => shiftWeek(-1)} className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-1.5 text-sm font-semibold rounded-lg" style={{ backgroundColor: "var(--tga-bg-elevated)", color: "var(--tga-text-primary)" }}
            data-testid="week-label"
          >
            KW {week.split("-W")[1]} · {monday.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – {new Date(monday.getTime() + 6 * 86400000).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
          <button onClick={() => shiftWeek(1)} className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--tga-blue)" }} />
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Mitarbeiter</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: "var(--tga-text-primary)" }}>{data.employees?.length || 0}</p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Gesamtarbeit</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">
                {fmtDurationFull(data.employees?.reduce((s, e) => s + (e.totals?.work_seconds || 0), 0) || 0)}
              </p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Utensils className="w-4 h-4 text-orange-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Verpflegung</span>
              </div>
              <p className="text-2xl font-bold text-orange-400">{data.totals?.meal_allowance || 0} EUR</p>
            </div>
          </div>

          {/* Table */}
          {(!data.employees || data.employees.length === 0) ? (
            <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--tga-text-dim)" }} />
              <p className="text-sm" style={{ color: "var(--tga-text-muted)" }}>Keine Mitarbeiter mit gefuehrtem Arbeitstag aktiv</p>
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ minWidth: 800 }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--tga-bg-elevated)", borderBottom: "2px solid var(--tga-border-strong)" }}>
                      <th className="text-left text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5 sticky left-0 z-10"
                        style={{ color: "var(--tga-text-muted)", backgroundColor: "var(--tga-bg-elevated)", minWidth: 160 }}>
                        Mitarbeiter
                      </th>
                      {data.dates?.map((d, i) => {
                        const dt = new Date(d + "T00:00:00");
                        const isToday = d === new Date().toISOString().split("T")[0];
                        return (
                          <th key={d} className="text-center text-[11px] font-semibold uppercase tracking-wider px-2 py-2.5"
                            style={{ color: isToday ? "var(--tga-blue)" : "var(--tga-text-muted)", minWidth: 90 }}>
                            <div>{DAY_NAMES[i]}</div>
                            <div className="text-[10px] font-normal">{dt.getDate()}.{dt.getMonth() + 1}.</div>
                          </th>
                        );
                      })}
                      <th className="text-center text-[11px] font-semibold uppercase tracking-wider px-3 py-2.5"
                        style={{ color: "var(--tga-text-muted)", minWidth: 80 }}>Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.employees.map((emp) => (
                      <tr key={emp.user_id} style={{ borderBottom: "1px solid var(--tga-border)" }} data-testid={`weekly-row-${emp.user_id}`}>
                        <td className="px-3 py-2.5 sticky left-0 z-10" style={{ backgroundColor: "var(--tga-bg-card)" }}>
                          <p className="text-[13px] font-medium truncate" style={{ color: "var(--tga-text-bright)" }}>{emp.user_name}</p>
                          <p className="text-[10px]" style={{ color: "var(--tga-text-dim)" }}>{emp.role}</p>
                        </td>
                        {emp.days?.map((day) => {
                          const hasData = day.work_seconds > 0 || day.travel_seconds > 0;
                          return (
                            <td key={day.date} className="px-2 py-2 text-center align-top">
                              {hasData ? (
                                <div className="space-y-0.5">
                                  {day.work_seconds > 0 && (
                                    <div className="flex items-center justify-center gap-1">
                                      <Briefcase className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                      <span className="text-[11px] font-mono font-medium text-emerald-400">{fmtDuration(day.work_seconds)}</span>
                                    </div>
                                  )}
                                  {day.travel_seconds > 0 && (
                                    <div className="flex items-center justify-center gap-1">
                                      <Car className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                      <span className="text-[11px] font-mono" style={{ color: "var(--tga-text-secondary)" }}>{fmtDuration(day.travel_seconds)}</span>
                                    </div>
                                  )}
                                  {day.pause_seconds > 0 && (
                                    <div className="flex items-center justify-center gap-1">
                                      <Coffee className="w-3 h-3 text-amber-400 flex-shrink-0" />
                                      <span className="text-[11px] font-mono" style={{ color: "var(--tga-text-dim)" }}>{fmtDuration(day.pause_seconds)}</span>
                                    </div>
                                  )}
                                  {day.meal_allowance > 0 && (
                                    <div className="flex items-center justify-center gap-1">
                                      <Utensils className="w-3 h-3 text-orange-400 flex-shrink-0" />
                                      <span className="text-[11px] font-bold text-orange-400">{day.meal_allowance} EUR</span>
                                    </div>
                                  )}
                                  {day.overnight && (
                                    <div className="flex items-center justify-center gap-1">
                                      <Hotel className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                                      <span className="text-[10px] text-indigo-400">Hotel</span>
                                    </div>
                                  )}
                                  {day.project_name && (
                                    <p className="text-[9px] truncate max-w-[80px] mx-auto" style={{ color: "var(--tga-text-dim)" }}>{day.project_name}</p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2.5 text-center" style={{ borderLeft: "2px solid var(--tga-border-strong)" }}>
                          <div className="space-y-0.5">
                            <p className="text-[12px] font-mono font-bold text-emerald-400">{fmtDurationFull(emp.totals?.work_seconds)}</p>
                            {emp.totals?.travel_seconds > 0 && (
                              <p className="text-[10px] font-mono" style={{ color: "var(--tga-text-secondary)" }}>
                                <Car className="w-3 h-3 text-blue-400 inline mr-0.5" />{fmtDuration(emp.totals.travel_seconds)}
                              </p>
                            )}
                            {emp.totals?.meal_allowance > 0 && (
                              <p className="text-[10px] font-bold text-orange-400">{emp.totals.meal_allowance} EUR</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
